// FILE: src/api/adminOpsApi.js
import { adminApi, adminJson } from "./adminHttp";

/* =============================================================================
   CORE HELPERS (stable + defensive)
============================================================================= */

function enc(v) {
  return encodeURIComponent(String(v ?? ""));
}

function normStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

/**
 * Normalize reason to { reasonCode, reasonText }.
 * Supports:
 * - "CODE" (string)
 * - {reasonCode, reasonText}
 * - {code, text}
 * - {code, label}
 * - {value, label} (common select shapes)
 */
function normReason(reason) {
  if (typeof reason === "string") {
    const code = normStr(reason);
    return { reasonCode: code, reasonText: "" };
  }
  const reasonCode =
    normStr(reason?.reasonCode) ||
    normStr(reason?.code) ||
    normStr(reason?.value) ||
    "";
  const reasonText =
    normStr(reason?.reasonText) ||
    normStr(reason?.text) ||
    normStr(reason?.label) ||
    "";
  return { reasonCode, reasonText };
}

/**
 * adminJson implementations sometimes expect:
 *   adminJson(url, { method, body, headers }) OR adminJson(url, { method, json })
 * Keep both `json` + `body` for safety.
 */
async function postJson(path, payload) {
  const p = payload ?? {};
  return adminJson(path, {
    method: "POST",
    json: p, // preferred
    body: p, // fallback
  });
}

function safeFallback(shape) {
  return () => shape;
}

function withContext(fnName, promise) {
  return promise.catch((e) => {
    try {
      e._opsApi = fnName;
    } catch {}
    throw e;
  });
}

/* =============================================================================
   CONFIG
============================================================================= */

export function opsGetReasons() {
  return withContext(
    "opsGetReasons",
    adminJson(`/admin/ops/config/reasons`).catch(safeFallback({ reasons: [] }))
  );
}

export function opsGetPolicyConsole() {
  return withContext(
    "opsGetPolicyConsole",
    adminJson(`/admin/ops/policies`).catch(
      safeFallback({ user: null, owner: null, driver: null, vendor: null })
    )
  );
}

export function opsGetPolicyPage(audience) {
  return withContext(
    "opsGetPolicyPage",
    adminJson(`/admin/ops/policies/${enc(audience)}`).catch(safeFallback(null))
  );
}

export function opsSavePolicyPage(audience, payload) {
  return withContext(
    "opsSavePolicyPage",
    postJson(`/admin/ops/policies/${enc(audience)}`, payload || {})
  );
}

/* =============================================================================
   DASHBOARD
============================================================================= */

export function opsGetDashboard({ window = 400 } = {}) {
  return withContext(
    "opsGetDashboard",
    adminJson(`/admin/ops/dashboard?window=${enc(window)}`).catch(
      safeFallback({ window, counts: {} })
    )
  );
}

/* =============================================================================
   BOOKINGS (monitor-only)
============================================================================= */

export function opsListBookings(opts = {}) {
  const { bucket, page = 0, size = 25, q = "", window = 400 } = opts;

  const qs = new URLSearchParams();
  qs.set("page", String(page ?? 0));
  qs.set("size", String(size ?? 25));
  qs.set("window", String(window ?? 400));

  const b = normStr(bucket);
  if (b && b.toUpperCase() !== "ALL") qs.set("bucket", b);
  if (q && normStr(q)) qs.set("q", normStr(q));

  return withContext(
    "opsListBookings",
    adminJson(`/admin/ops/bookings?${qs.toString()}`).catch(
      safeFallback({ bucket: b || null, page, size, total: 0, items: [] })
    )
  );
}

export function opsGetBookingDetail(bookingId) {
  return withContext(
    "opsGetBookingDetail",
    adminJson(`/admin/ops/bookings/${enc(bookingId)}`).catch(
      safeFallback({
        booking: null,
        notes: [],
        timeline: [],
        related: null,
        summary: {},
      })
    )
  );
}

export function opsGetBookingNotes(bookingId) {
  return withContext(
    "opsGetBookingNotes",
    opsGetBookingDetail(bookingId).then((d) => ({ notes: d?.notes || [] }))
  );
}

export function opsGetBookingTimeline(bookingId) {
  return withContext(
    "opsGetBookingTimeline",
    opsGetBookingDetail(bookingId).then((d) => ({ timeline: d?.timeline || [] }))
  );
}

export function opsAddBookingNote(bookingId, note, tags = [], reason) {
  const r = normReason(reason);
  return withContext(
    "opsAddBookingNote",
    postJson(`/admin/ops/bookings/${enc(bookingId)}/note`, {
      note: normStr(note),
      tags: Array.isArray(tags) ? tags : [],
      reason: { reasonCode: r.reasonCode, reasonText: r.reasonText },
    })
  );
}

export const opsAddNote = opsAddBookingNote;

/* =============================================================================
   DISPATCH (keep only if endpoints exist)
============================================================================= */

export function opsDispatchUnassigned({ windowMins = 360, window = 400 } = {}) {
  return withContext(
    "opsDispatchUnassigned",
    adminJson(
      `/admin/ops/dispatch/unassigned?windowMins=${enc(
        windowMins
      )}&window=${enc(window)}`
    ).catch(safeFallback({ items: [] }))
  );
}

export function opsDispatchDrivers({ q = "", limit = 50 } = {}) {
  const qs = new URLSearchParams();
  if (q && normStr(q)) qs.set("q", normStr(q));
  qs.set("limit", String(limit ?? 50));
  return withContext(
    "opsDispatchDrivers",
    adminJson(`/admin/ops/dispatch/drivers?${qs.toString()}`).catch(
      safeFallback({ drivers: [] })
    )
  );
}

/* =============================================================================
   STREAM (SSE)
============================================================================= */

export function opsStreamUrl({ window = 400 } = {}) {
  return `/admin/ops/stream?window=${enc(window)}`;
}

/* =============================================================================
   REQUESTS (keep only if your AdminOpsRequestController exists)
============================================================================= */

export function opsListRequests({ page = 0, size = 25, q = "" } = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page ?? 0));
  qs.set("size", String(size ?? 25));
  if (q && normStr(q)) qs.set("q", normStr(q));

  return withContext(
    "opsListRequests",
    adminJson(`/admin/ops/requests?${qs.toString()}`).catch(
      safeFallback({ page, size, total: 0, items: [] })
    )
  );
}

export function opsGetRequestDetail(id) {
  return withContext("opsGetRequestDetail", adminJson(`/admin/ops/requests/${enc(id)}`));
}

export function opsRequestRerun(id, reason) {
  const r = normReason(reason);
  return withContext(
    "opsRequestRerun",
    postJson(`/admin/ops/requests/${enc(id)}/rerun`, {
      reasonCode: r.reasonCode,
      reasonText: r.reasonText,
    })
  );
}

export function opsRequestCancel(id, reason) {
  const r = normReason(reason);
  return withContext(
    "opsRequestCancel",
    postJson(`/admin/ops/requests/${enc(id)}/cancel`, {
      reasonCode: r.reasonCode,
      reasonText: r.reasonText,
    })
  );
}

export function opsAddRequestNote(id, note, tags = [], reason) {
  const r = normReason(reason);
  return withContext(
    "opsAddRequestNote",
    postJson(`/admin/ops/requests/${enc(id)}/note`, {
      note: normStr(note),
      tags: Array.isArray(tags) ? tags : [],
      reasonCode: r.reasonCode,
      reasonText: r.reasonText,
    })
  );
}

export const opsListMatchRequests = opsListRequests;
export const opsGetMatchRequestDetail = opsGetRequestDetail;

/* =============================================================================
   AUDIT LOGS
============================================================================= */

export function opsAuditLogs(a, b, c, d) {
  if (a && typeof a === "object") {
    const { entityType, entityId, page = 0, size = 50 } = a;
    const qs = new URLSearchParams();
    if (entityType) qs.set("entityType", normStr(entityType));
    if (entityId != null) qs.set("entityId", String(entityId));
    qs.set("page", String(page ?? 0));
    qs.set("size", String(size ?? 50));
    return withContext(
      "opsAuditLogs",
      adminJson(`/admin/ops/audit-logs?${qs.toString()}`).catch(
        safeFallback({ content: [] })
      )
    );
  }

  const entityType = a;
  const entityId = b;
  const page = c ?? 0;
  const size = d ?? 50;

  const qs = new URLSearchParams();
  if (entityType) qs.set("entityType", normStr(entityType));
  if (entityId != null) qs.set("entityId", String(entityId));
  qs.set("page", String(page));
  qs.set("size", String(size));

  return withContext(
    "opsAuditLogs",
    adminJson(`/admin/ops/audit-logs?${qs.toString()}`).catch(
      safeFallback({ content: [] })
    )
  );
}

export const opsListAuditLogs = opsAuditLogs;

/* =============================================================================
   RIDES (monitor-only) ✅ matches AdminOpsRideController (adjusted)
   - GET  /admin/ops/rides
     -> { bucket, page, size, total, items: AdminRideRowDTO[] }
   - GET  /admin/ops/rides/{id}
     -> { ride, row, case, notes, issues, timeline, recentLocations, summary }
   - POST /admin/ops/rides/{id}/notes (compat: /note)
   - POST /admin/ops/rides/{id}/issue
   - POST /admin/ops/rides/{id}/case/resolve
   - POST /admin/ops/rides/{id}/reconcile
============================================================================= */

export function opsListRides(opts = {}) {
  const { bucket, page = 0, size = 25, q = "", window = 400 } = opts;

  const qs = new URLSearchParams();
  qs.set("page", String(page ?? 0));
  qs.set("size", String(size ?? 25));
  qs.set("window", String(window ?? 400));

  const b = normStr(bucket);
  if (b && b.toUpperCase() !== "ALL") qs.set("bucket", b);
  if (q && normStr(q)) qs.set("q", normStr(q));

  return withContext(
    "opsListRides",
    adminJson(`/admin/ops/rides?${qs.toString()}`).catch(
      safeFallback({ bucket: b || null, page, size, total: 0, items: [] })
    )
  );
}

export function opsGetRideDetail(id) {
  return withContext(
    "opsGetRideDetail",
    adminJson(`/admin/ops/rides/${enc(id)}`).catch(
      safeFallback({
        ride: null,
        row: null,
        case: null,
        notes: [],
        issues: [],
        timeline: [],
        recentLocations: [],
        summary: null,
      })
    )
  );
}

/** POST /admin/ops/rides/{id}/issue  Body: { reasonCode, reasonText } */
export function opsRideMarkIssue(id, reason) {
  const r = normReason(reason);
  return withContext(
    "opsRideMarkIssue",
    postJson(`/admin/ops/rides/${enc(id)}/issue`, {
      reasonCode: r.reasonCode,
      reasonText: r.reasonText,
    })
  );
}

/** POST /admin/ops/rides/{id}/notes  Body: OpsNoteRequest shape */
export function opsAddRideNote(id, note, tags = [], reason) {
  const r = normReason(reason);
  return withContext(
    "opsAddRideNote",
    postJson(`/admin/ops/rides/${enc(id)}/notes`, {
      note: normStr(note),
      tags: Array.isArray(tags) ? tags : [],
      reason: { reasonCode: r.reasonCode, reasonText: r.reasonText },
    })
  );
}

/** POST /admin/ops/rides/{id}/case/resolve Body: { reasonCode, reasonText } */
export function opsRideResolveCase(id, reason) {
  const r = normReason(reason);
  return withContext(
    "opsRideResolveCase",
    postJson(`/admin/ops/rides/${enc(id)}/case/resolve`, {
      reasonCode: r.reasonCode,
      reasonText: r.reasonText,
    })
  );
}

/** POST /admin/ops/rides/{id}/reconcile  (no body) */
export function opsRideReconcile(id) {
  return withContext(
    "opsRideReconcile",
    postJson(`/admin/ops/rides/${enc(id)}/reconcile`, {})
  );
}

export function opsGetSearchMode() {
  return withContext(
    "opsGetSearchMode",
    adminJson(`/admin/ops/config/search-mode`).catch(
      safeFallback({ mode: "DATABASE", databaseEnabled: true, jsonCatalogEnabled: false })
    )
  );
}

export function opsSetSearchMode(modeOrPayload) {
  const payload =
    modeOrPayload && typeof modeOrPayload === "object"
      ? modeOrPayload
      : { mode: normStr(modeOrPayload) };
  return withContext(
    "opsSetSearchMode",
    postJson(`/admin/ops/config/search-mode`, payload)
  );
}

export function opsGetAppVersionConfig() {
  return withContext(
    "opsGetAppVersionConfig",
    adminJson(`/admin/ops/config/app-version`).catch(
      safeFallback({
        consumer: { minimumRequiredBuildVersion: 0, latestBuildVersion: null, updateMessage: "" },
        driver: { minimumRequiredBuildVersion: 0, latestBuildVersion: null, updateMessage: "" },
        vendor: { minimumRequiredBuildVersion: 0, latestBuildVersion: null, updateMessage: "" },
        support: { phone: "", whatsapp: "" },
      })
    )
  );
}

export function opsSetAppVersionConfig(payload) {
  return withContext(
    "opsSetAppVersionConfig",
    postJson(`/admin/ops/config/app-version`, payload || {})
  );
}

export function opsGetVehicleCatalog() {
  return withContext(
    "opsGetVehicleCatalog",
    adminJson(`/admin/ops/config/catalog`).catch(safeFallback({ catalog: {} }))
  );
}

export function opsSaveVehicleCatalog(catalog) {
  return withContext(
    "opsSaveVehicleCatalog",
    postJson(`/admin/ops/config/catalog`, { catalog: catalog || {} })
  );
}

export async function opsUploadVehicleCatalogImage(file, { brand = "", model = "", color = "" } = {}) {
  const form = new FormData();
  form.append("file", file);
  form.append("brand", normStr(brand));
  form.append("model", normStr(model));
  if (normStr(color)) {
    form.append("color", normStr(color));
  }
  const res = await adminApi(`/admin/ops/config/catalog/upload-image`, {
    method: "POST",
    body: form,
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data?.message || data?.error || "Image upload failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function opsListTripRequests({ bucket = "ALL", q = "" } = {}) {
  const qs = new URLSearchParams();
  const b = normStr(bucket);
  if (b && b.toUpperCase() !== "ALL") qs.set("bucket", b);
  if (q && normStr(q)) qs.set("q", normStr(q));
  return withContext(
    "opsListTripRequests",
    adminJson(`/admin/ops/trip-requests?${qs.toString()}`).catch(safeFallback({ items: [] }))
  );
}

const TRIP_REQUEST_UNIVERSE_BUCKETS = [
  "REQUEST_PENDING_ATTENTION",
  "REQUEST_PENDING",
  "CONFIRMED",
  "COMPLETED",
  "EXPIRED",
  "UNFULFILLED",
];

const tripRequestUniverseCache = new Map();

function mergeTripRequestBucketResults(results = []) {
  const merged = results.flatMap((res) => (Array.isArray(res?.items) ? res.items : []));
  const byId = new Map();
  merged.forEach((item) => {
    if (item?.id != null) byId.set(item.id, item);
  });
  return Array.from(byId.values());
}

export function opsListTripRequestBuckets({ buckets = [], q = "", force = false } = {}) {
  const bucketList = Array.from(new Set((Array.isArray(buckets) ? buckets : []).map((bucket) => normStr(bucket)).filter(Boolean)));
  const normalizedBuckets = bucketList.length ? bucketList : TRIP_REQUEST_UNIVERSE_BUCKETS;
  const key = `${normStr(q).toLowerCase()}::${normalizedBuckets.join("|")}`;
  const now = Date.now();
  const cached = tripRequestUniverseCache.get(key);

  if (!force && cached?.items && now - Number(cached.ts || 0) < 15000) {
    return Promise.resolve(cached.items);
  }

  if (!force && cached?.promise) {
    return cached.promise;
  }

  const promise = Promise.all(
    normalizedBuckets.map((bucket) =>
      opsListTripRequests({ bucket, q }).catch(safeFallback({ items: [] }))
    )
  )
    .then((results) => {
      const items = mergeTripRequestBucketResults(results);
      tripRequestUniverseCache.set(key, {
        ts: Date.now(),
        items,
      });
      return items;
    })
    .catch((error) => {
      tripRequestUniverseCache.delete(key);
      throw error;
    });

  tripRequestUniverseCache.set(key, {
    ts: cached?.ts || 0,
    items: cached?.items || null,
    promise,
  });

  return promise;
}

export function opsListTripRequestUniverse({ q = "", force = false } = {}) {
  return opsListTripRequestBuckets({
    buckets: TRIP_REQUEST_UNIVERSE_BUCKETS,
    q,
    force,
  });
}

export function opsGetTripRequestDetail(id) {
  return withContext(
    "opsGetTripRequestDetail",
    adminJson(`/admin/ops/trip-requests/${enc(id)}`).catch(safeFallback({ request: null }))
  );
}

export function opsUpdateTripRequestBookingRecovery(id, { cases = [] } = {}) {
  return withContext(
    "opsUpdateTripRequestBookingRecovery",
    postJson(`/admin/ops/trip-requests/${enc(id)}/booking-recovery`, {
      cases: Array.isArray(cases) ? cases : [],
    })
  );
}

export function opsUpdateTripRequestBookingOutcomes(id, { cases = [] } = {}) {
  return withContext(
    "opsUpdateTripRequestBookingOutcomes",
    postJson(`/admin/ops/trip-requests/${enc(id)}/booking-outcomes`, {
      cases: Array.isArray(cases) ? cases : [],
    })
  );
}

export function opsUpdateTripCaseStatus(caseId, status, note = "") {
  return withContext(
    "opsUpdateTripCaseStatus",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/status`, {
      status: normStr(status),
      note: normStr(note),
    })
  );
}

export function opsOutsourceTripCase(caseId, { vendorName = "", vendorPhone = "", note = "" } = {}) {
  return withContext(
    "opsOutsourceTripCase",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/outsource`, {
      vendorName: normStr(vendorName),
      vendorPhone: normStr(vendorPhone),
      note: normStr(note),
    })
  );
}

export function opsAssignTripCaseVehicle(caseId, { regNo = "", note = "", generateOffer = false } = {}) {
  return withContext(
    "opsAssignTripCaseVehicle",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/assign-vehicle`, {
      regNo: normStr(regNo),
      note: normStr(note),
      generateOffer: !!generateOffer,
    })
  );
}

export function opsListVendors({ q = "" } = {}) {
  const qs = new URLSearchParams();
  if (q && normStr(q)) qs.set("q", normStr(q));
  return withContext(
    "opsListVendors",
    adminJson(`/admin/ops/trip-requests/vendors?${qs.toString()}`).catch(safeFallback({ items: [] }))
  );
}

export function opsCreateVendor({ name = "", phone = "", brand = "", model = "", registrationNumber = "" } = {}) {
  return withContext(
    "opsCreateVendor",
    postJson(`/admin/ops/trip-requests/vendors`, {
      name: normStr(name),
      phone: normStr(phone),
      brand: normStr(brand),
      model: normStr(model),
      registrationNumber: normStr(registrationNumber),
    })
  );
}

export function opsGetVendorDetail(vendorId) {
  return withContext(
    "opsGetVendorDetail",
    adminJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}`).catch(safeFallback(null))
  );
}

export function opsGetVendorPenalties(vendorId) {
  return withContext(
    "opsGetVendorPenalties",
    adminJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/penalties`)
      .then((res) => {
        const summary = res?.summary && typeof res.summary === "object" ? { ...res.summary } : {};
        const penalties = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.penalties)
            ? res.penalties
            : Array.isArray(summary?.penalties)
              ? summary.penalties
              : [];
        return {
          blocked: !!summary?.blocked,
          outstandingPenaltyCount: Number(summary?.outstandingPenaltyCount || 0),
          outstandingPenaltyAmount: Number(summary?.outstandingPenaltyAmount || 0),
          latestReasonCode: summary?.latestReasonCode || null,
          latestTriggerType: summary?.latestTriggerType || null,
          latestPenaltyId: summary?.latestPenaltyId || null,
          latestPickupAt: summary?.latestPickupAt || null,
          message: summary?.message || null,
          penalties,
        };
      })
      .catch(safeFallback({ blocked: false, outstandingPenaltyCount: 0, outstandingPenaltyAmount: 0, penalties: [] }))
  );
}

export function opsPushVendorPenalty(
  vendorId,
  {
    allocationId = null,
    bookingId = null,
    tripRequestId = null,
    planKey = "",
    pickupAt = "",
    amount = null,
    note = "",
  } = {}
) {
  return withContext(
    "opsPushVendorPenalty",
    postJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/penalties/push`, {
      allocationId,
      bookingId,
      tripRequestId,
      planKey: normStr(planKey),
      pickupAt: normStr(pickupAt),
      amount,
      note: normStr(note),
    })
  );
}

export function opsEnableVendorWithoutPayment(vendorId, { note = "" } = {}) {
  return withContext(
    "opsEnableVendorWithoutPayment",
    postJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/enable`, {
      note: normStr(note),
    })
  );
}

export function opsAdjustVendorPenaltyAmount(vendorId, penaltyId, { amount = null, note = "" } = {}) {
  return withContext(
    "opsAdjustVendorPenaltyAmount",
    postJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/penalties/${enc(penaltyId)}/amount`, {
      amount,
      note: normStr(note),
    })
  );
}

export function opsSaveVendor({
  id = null,
  name = "",
  phone = "",
  email = "",
  companyName = "",
  baseLocation = "",
  profileImageUrl = "",
  active = true,
} = {}) {
  return withContext(
    "opsSaveVendor",
    postJson(`/admin/ops/trip-requests/vendors`, {
      id,
      name: normStr(name),
      phone: normStr(phone),
      email: normStr(email),
      companyName: normStr(companyName),
      baseLocation: normStr(baseLocation),
      profileImageUrl: normStr(profileImageUrl),
      active: !!active,
    })
  );
}

export function opsSaveVendorVehicle(vendorId, {
  id = null,
  brand = "",
  model = "",
  registrationNumber = "",
  color = "",
  baseLocation = "",
  baseLat = null,
  baseLng = null,
  active = true,
  consentAccepted = null,
} = {}) {
  return withContext(
    "opsSaveVendorVehicle",
    postJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/vehicles`, {
      id,
      brand: normStr(brand),
      model: normStr(model),
      registrationNumber: normStr(registrationNumber),
      color: normStr(color),
      baseLocation: normStr(baseLocation),
      baseLat,
      baseLng,
      active: !!active,
      consentAccepted,
    })
  );
}

export function opsGetVendorVehicleConsent(vendorId, vehicleId) {
  return withContext(
    "opsGetVendorVehicleConsent",
    adminJson(`/admin/ops/trip-requests/vendors/${enc(vendorId)}/vehicles/${enc(vehicleId)}/consent`).catch(safeFallback(null))
  );
}

export function opsSaveCaseFulfillment(
  caseId,
  {
    sourceType = "",
    status = "",
    assigned = false,
    vendorId = null,
    vendorVehicleId = null,
    vendorName = "",
    vendorPhone = "",
    vendorVehicleBrand = "",
    vendorVehicleModel = "",
    vendorVehicleRegNo = "",
    note = "",
  } = {}
) {
  return withContext(
    "opsSaveCaseFulfillment",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/fulfillment`, {
      sourceType: normStr(sourceType),
      status: normStr(status),
      assigned: !!assigned,
      vendorId,
      vendorVehicleId,
      vendorName: normStr(vendorName),
      vendorPhone: normStr(vendorPhone),
      vendorVehicleBrand: normStr(vendorVehicleBrand),
      vendorVehicleModel: normStr(vendorVehicleModel),
      vendorVehicleRegNo: normStr(vendorVehicleRegNo),
      note: normStr(note),
    })
  );
}

export function opsRunTripCaseOffer(caseId, { note = "" } = {}) {
  return withContext(
    "opsRunTripCaseOffer",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/run-offer`, {
      note: normStr(note),
    })
  );
}

export function opsRunTripCaseTargetOffer(
  caseId,
  {
    recipientType = "",
    regNo = "",
    vendorId = null,
    vendorVehicleId = null,
    note = "",
  } = {}
) {
  return withContext(
    "opsRunTripCaseTargetOffer",
    postJson(`/admin/ops/trip-requests/cases/${enc(caseId)}/target-offer`, {
      recipientType: normStr(recipientType),
      regNo: normStr(regNo),
      vendorId,
      vendorVehicleId,
      note: normStr(note),
    })
  );
}
